/**
 * @file client.ts
 *
 * Central fetch wrapper for commerce-engine API calls. It prefixes the resolved
 * API base URL, attaches the in-memory frontend token as a Bearer header (token
 * mode), always sends credentials so the session cookie travels (password mode),
 * and sets a custom header so a forged cross-site request can't satisfy it
 * without a CORS preflight (CSRF defense-in-depth). A 401 invokes the registered
 * unauthorized handler so the app can route back to its auth entry point.
 */

import { COMMERCE_ENGINE_URL } from '../config';

let authToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

/**
 * Sets (or clears) the in-memory frontend token sent as a Bearer credential.
 * Never persisted, so a reload drops it — intended for the token (iframe) flow.
 *
 * @param token - The frontend token, or `null` to clear it.
 */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/**
 * Registers the callback invoked when an API call returns 401, letting the app
 * redirect to the login page or unauthorized screen.
 *
 * @param handler - The unauthorized callback.
 */
export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

/**
 * Performs an authenticated request against the commerce-engine API.
 *
 * @param path - API path beginning with `/` (the base URL is prefixed).
 * @param init - Standard fetch options; headers are merged with the auth headers.
 *
 * @returns The raw {@link Response}; callers inspect `ok`/`status`.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('X-Requested-With', 'fetch');
  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  const response = await fetch(`${COMMERCE_ENGINE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (response.status === 401 && unauthorizedHandler) {
    unauthorizedHandler();
  }
  return response;
}
